import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import { WINDOW } from "@tylertech/shared-module";
import { IMetadataAction, KeyValuePair } from "@tylertech/tyler-api-types";
import { firstValueFrom, fromEvent } from 'rxjs';
import { filter, timeout } from "rxjs/operators";
import { MetadataService } from "./metadata.service";
import { MetadataViewService } from "./metadataViewService";

@Injectable({ providedIn: "root" })
export class MetadataIframeService
{
    constructor(
        @Inject(WINDOW) private readonly window: Window,
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly $metadataService: MetadataService,
        private readonly $metadataViewService: MetadataViewService,
    ) { }

    get insideIframe(): boolean
    {
        try
        {
            return this.window.self !== this.window.top;
        }
        catch (e)
        {
            // browser may throw error when iframe window and original window aren't in the same domain
            // in which case it should confirm this is in an iframe
            return true;
        }
    }

    MaybeResponseToParentWindow(saved: boolean): void
    {
        if (this.insideIframe)
        {
            const message: KeyValuePair<string, any> =
            {
                Key: "metadataIframeCloseResponse",
                Value: saved
            };
            this.PostMessageToParent(message);
        }
    }

    protected PostMessageToParent(message: KeyValuePair<string, any>): void
    {
        this.window.parent.postMessage(message, "*");
    }

    FindMetadataWindowHostedInIframe(action: IMetadataAction, model: any): Window
    {
        if (!this.$metadataViewService.IsExternalUrl(action.ViewToShow))
        {
            return null;
        }

        // Only do this if the target window hosts a metadata form
        // At this point, we only need this to work for the form app
        const targetUrl = this.$metadataService.EvaluateString(action.ViewToShow["ExternalUrlProperty"], null, model, null, true);
        if (!targetUrl || targetUrl.toLowerCase().indexOf('/apps/form/') < 0)
        {
            return null;
        }

        // Find the matching iframe window assuming we won't have multiple iframes pointing to the same url.
        const hashId = this.hashExternalUrl(targetUrl);
        const targetIframe = this.document.querySelector('iframe[data-hash="' + hashId + '"]') as HTMLIFrameElement;

        return targetIframe?.contentWindow;
    }

    hashExternalUrl(url: string): number
    {
        let hash = 0;
        for (let i = 0; i < url.length; i++)
        {
            let chr = url.charCodeAt(i);
            hash = ((hash << 5) - hash) + chr;
            hash |= 0;
        }
        return hash;
    }

    PostMessageToIframeAndGetResponse(
        contentWindow: Window,
        message: KeyValuePair<string, any>,
        responseMessageKey: string,
        timeoutMs: number): Promise<MessageEvent<KeyValuePair<string, any>>>
    {
        // This assumes the target window won't respond before we start the listener
        contentWindow.postMessage(message, "*");

        // We only needs the 1st response, so returning a Promise is fine.
        return firstValueFrom(
            fromEvent(this.window, 'message')
                .pipe(
                    // takeUntil(self.destructor$),
                    timeout(timeoutMs),
                    filter((messageEvent: MessageEvent<KeyValuePair<string, any>>) =>
                    {
                        return messageEvent.data && messageEvent.data.Key === responseMessageKey;
                    })));
    }
}
